import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  RefreshControl,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Text, ActivityIndicator, useTheme } from 'react-native-paper';
import Toast from 'react-native-toast-message';
import {
  useRecipeComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useLikeComment,
  useUnlikeComment,
} from '@coffeeplexity/api-client';
import { useAppSelector } from '../../store';
import { CommentItem } from './CommentItem';
import { CommentInput } from './CommentInput';
import type { Comment } from '@coffeeplexity/shared';

interface CommentListProps {
  recipeId: string;
}

export function CommentList({ recipeId }: CommentListProps) {
  const theme = useTheme();
  const { user } = useAppSelector(state => state.auth);

  const [editingComment, setEditingComment] = useState<Comment | null>(null);

  // Queries and mutations
  const { data, isLoading, error, refetch, isRefetching } =
    useRecipeComments(recipeId);
  const createComment = useCreateComment();
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();
  const likeComment = useLikeComment();
  const unlikeComment = useUnlikeComment();

  const comments = data?.comments || [];

  const handleAddComment = async (text: string) => {
    if (!text.trim()) {
      Toast.show({
        type: 'error',
        text1: 'Invalid comment',
        text2: 'Comment cannot be empty',
      });
      return;
    }

    try {
      await createComment.mutateAsync({ recipeId, content: text });
      Toast.show({
        type: 'success',
        text1: 'Comment added',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to add comment',
        text2: 'Please try again',
      });
    }
  };

  const handleUpdateComment = async (text: string) => {
    if (!editingComment) return;

    if (!text.trim()) {
      Toast.show({
        type: 'error',
        text1: 'Invalid comment',
        text2: 'Comment cannot be empty',
      });
      return;
    }

    try {
      await updateComment.mutateAsync({
        commentId: editingComment.id,
        content: text,
      });
      setEditingComment(null);
      Toast.show({
        type: 'success',
        text1: 'Comment updated',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to update comment',
        text2: 'Please try again',
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment.mutateAsync(commentId);
      Toast.show({
        type: 'success',
        text1: 'Comment deleted',
      });
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to delete comment',
        text2: 'Please try again',
      });
    }
  };

  const handleLikeComment = async (commentId: string) => {
    try {
      await likeComment.mutateAsync(commentId);
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to like comment',
      });
    }
  };

  const handleUnlikeComment = async (commentId: string) => {
    try {
      await unlikeComment.mutateAsync(commentId);
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Failed to unlike comment',
      });
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingComment(comment);
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
  };

  if (isLoading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size='large' color={theme.colors.primary} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centerContainer}>
        <Text variant='bodyLarge' style={{ color: theme.colors.error }}>
          Failed to load comments
        </Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
    >
      <View style={styles.header}>
        <Text
          variant='titleMedium'
          style={{ color: theme.colors.onBackground }}
        >
          Comments ({comments.length})
        </Text>
      </View>

      <FlatList
        data={comments}
        keyExtractor={item => item.id}
        renderItem={({ item }) => (
          <CommentItem
            comment={item}
            currentUserId={user?.id}
            onEdit={handleEditComment}
            onDelete={handleDeleteComment}
            onLike={handleLikeComment}
            onUnlike={handleUnlikeComment}
            isLikeLoading={likeComment.isPending || unlikeComment.isPending}
          />
        )}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text
              variant='bodyLarge'
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              No comments yet. Be the first to comment!
            </Text>
          </View>
        }
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={refetch}
            colors={[theme.colors.primary]}
          />
        }
      />

      <View
        style={[
          styles.inputContainer,
          { backgroundColor: theme.colors.surface },
        ]}
      >
        <CommentInput
          onSubmit={editingComment ? handleUpdateComment : handleAddComment}
          initialValue={
            editingComment ? editingComment.text || editingComment.content : ''
          }
          placeholder={editingComment ? 'Edit comment...' : 'Add a comment...'}
          submitLabel={editingComment ? 'Update' : 'Post'}
          onCancel={editingComment ? handleCancelEdit : undefined}
        />
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  listContent: {
    padding: 16,
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  inputContainer: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
    padding: 12,
  },
});
